import { Button, Heading, PulseLoader, Reply, Text } from '@sparemin/blockhead';
import React, { useCallback } from 'react';
import LinkButton from 'components/LinkButton';
import useRunInDeferredTab from 'hooks/useRunInDeferredTab';
import bem from 'utils/bem';
import { DEFAULT_PREPARING_CLIP_TAB_MESSAGE } from '../../constants';
import { useClipSelect } from '../ClipSelectContext';
import { useClipSelectDetailCardContext } from './ClipSelectDetailCardContext';
import { ClipSelectDetailCardCTAIntent } from './types';

const block = bem('clip-select-detail-card-loading-overlay');

export type ClipSelectDetailLoadingOverlayProps = {
  onClose: () => void;
  variant?: ClipSelectDetailCardCTAIntent;
};

const ClipSelectDetailLoadingOverlay: React.FC<ClipSelectDetailLoadingOverlayProps> = ({
  onClose,
  variant = 'share',
}) => {
  const { suggestionId } = useClipSelectDetailCardContext();
  const { isExporting, prepareShareUrl } = useClipSelect({
    clipId: suggestionId,
  });
  const runInTab = useRunInDeferredTab(DEFAULT_PREPARING_CLIP_TAB_MESSAGE);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleShare = useCallback(() => {
    handleClose();
    runInTab(prepareShareUrl);
  }, [handleClose, prepareShareUrl, runInTab]);

  return (
    <div className={block()}>
      {isExporting ? (
        <>
          <Heading level={1}>
            {variant === 'share'
              ? 'Exporting Video'
              : 'Opening in Advanced Editor'}
          </Heading>
          <Text className={block('description')} variant="p">
            This could take a couple minutes.
          </Text>
          <PulseLoader className={block('spinner')} color="white" />
        </>
      ) : (
        <>
          <Heading level={1}>Your clip is ready!</Heading>
          <Button
            className={block('cta', { primary: true })}
            onPress={handleShare}
            startIcon={<Reply />}
            variant="special"
          >
            Share
          </Button>
          <LinkButton
            className={block('cta', { secondary: true })}
            onClick={handleClose}
            theme="cta"
            uppercase
          >
            continue editing
          </LinkButton>
        </>
      )}
    </div>
  );
};

export default ClipSelectDetailLoadingOverlay;
